import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { GroupedViewRequirementIntake, IntakeStatus, VerdictResult } from 'api';
import { IntakeStatePillComponent } from 'component/details/requirement-intake/intake-state-pill/intake-state-pill.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

interface RequirementGroupViewModel {
  readonly category: string;
  readonly categoryNumber: number;
  readonly decidedCount: number;
  readonly requirements: ReadonlyArray<RequirementNodeViewModel>;
}

interface RequirementNodeViewModel {
  readonly id: number;
  readonly name: string;
  readonly status: IntakeStatus;
  readonly verdictResult?: VerdictResult;
  readonly evidenceCount: number;
}

@Component({
  selector: 'comp-requirement-tree',
  templateUrl: './requirement-tree.component.html',
  styleUrls: ['./requirement-tree.component.scss'],
  imports: [IntakeStatePillComponent, TranslateModule, MatProgressSpinner, MatTooltip],
})
export class RequirementTreeComponent {
  readonly revisionId = input<number | undefined>(undefined);
  readonly selectedRequirementId = input<number | undefined>(undefined);
  readonly groupedRequirements = input<ReadonlyArray<GroupedViewRequirementIntake>>([]);
  readonly isLoading = input<boolean>(false);
  readonly hasError = input<boolean>(false);
  readonly requirementStatusOverrides = input<Record<number, IntakeStatus>>({});
  readonly autoSelectFirstTodo = input<boolean>(true);

  readonly requirementSelected = output<number>();

  protected readonly groups = computed<ReadonlyArray<RequirementGroupViewModel>>(() =>
    this.groupedRequirements().map((group, groupIndex) => {
      const requirements = group.items.map((requirement) => ({
        id: requirement.id,
        name: requirement.name,
        status: requirement.intakeStatus ?? IntakeStatus.Incomplete,
        verdictResult: this.resolveVerdictResult(requirement.verdict),
        evidenceCount: requirement.evidences.filter(
          (ev) => ev.mandatory || ev.fileAttachments.length,
        ).length,
      }));

      var nameSplitted = group.groupName.split(':');
      return {
        category: nameSplitted.length > 1 ? nameSplitted[1] : group.groupName,
        categoryNumber: nameSplitted.length > 1 ? parseInt(nameSplitted[0], 10) : groupIndex + 1,
        decidedCount: requirements.filter(
          (requirement) =>
            requirement.status === IntakeStatus.Decided ||
            requirement.status === IntakeStatus.NotApplicable,
        ).length,
        requirements,
      };
    }),
  );

  protected readonly isEmpty = computed(
    () => this.groups().length === 0 && !this.hasError() && !this.isLoading(),
  );

  protected readonly expandedCategory = signal<string | undefined>(undefined);
  private readonly autoSelectionCompletedForRevisionId = signal<number | undefined>(undefined);
  private readonly categoryElements = viewChildren<ElementRef>('categoryElement');

  constructor() {
    // When the revision changes, reset state.
    effect(() => {
      this.revisionId();
      this.expandedCategory.set(undefined);
      this.autoSelectionCompletedForRevisionId.set(undefined);
    });

    // When groups load and nothing is expanded yet, open the group that contains
    // the currently selected requirement, or fall back to the first group.
    effect(() => {
      const selectedRequirementId = this.selectedRequirementId();
      if (selectedRequirementId === undefined) {
        return;
      }

      const groups = this.groups();

      const groupWithSelection = selectedRequirementId
        ? groups.find((group) => group.requirements.some((r) => r.id === selectedRequirementId))
        : undefined;

      this.expandedCategory.set((groupWithSelection ?? groups[0]).category);
    });

    // When the selected requirement changes (e.g. via auto-selection or user navigation),
    // ensure its group is the one that is open.
    effect(() => {
      const selectedRequirementId = this.selectedRequirementId();
      if (selectedRequirementId === undefined) {
        return;
      }

      const groups = this.groups();
      const groupWithSelection = groups.find((group) =>
        group.requirements.some((r) => r.id === selectedRequirementId),
      );

      if (groupWithSelection) {
        this.expandedCategory.set(groupWithSelection.category);
      }
    });

    // Auto-select the first Todo requirement for a new revision.
    effect(() => {
      const revisionId = this.revisionId();
      const selectedRequirementId = this.selectedRequirementId();
      const groups = this.groups();
      const autoSelectFirstTodo = this.autoSelectFirstTodo();

      if (
        revisionId === undefined ||
        groups.length === 0 ||
        selectedRequirementId !== undefined ||
        !autoSelectFirstTodo
      ) {
        return;
      }

      if (this.autoSelectionCompletedForRevisionId() === revisionId) {
        return;
      }

      const firstTodoRequirement = groups
        .flatMap((group) => group.requirements)
        .find((requirement) => requirement.status === IntakeStatus.Incomplete);

      this.autoSelectionCompletedForRevisionId.set(revisionId);

      if (firstTodoRequirement) {
        this.requirementSelected.emit(firstTodoRequirement.id);
      }
    });

    effect(() => {
      const expandedCategory = this.expandedCategory();
      if (expandedCategory === undefined) {
        return;
      }

      const categoryElement = this.categoryElements().find(
        (element) => element.nativeElement.dataset['category'] === expandedCategory,
      );

      categoryElement?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }

  protected isExpanded(category: string): boolean {
    return this.expandedCategory() === category;
  }

  protected toggleCategory(category: string): void {
    this.expandedCategory.update((current) => (current === category ? undefined : category));
  }

  protected openRequirement(requirementId: number): void {
    this.requirementSelected.emit(requirementId);
  }

  protected isSelected(requirementId: number): boolean {
    return this.selectedRequirementId() === requirementId;
  }

  protected hasSelectedItem(category: string): boolean {
    const selectedId = this.selectedRequirementId();
    if (selectedId === undefined) {
      return false;
    }

    const group = this.groups().find((g) => g.category === category);
    return group?.requirements.some((r) => r.id === selectedId) ?? false;
  }

  protected hasItemWithReviewFindings(category: string): boolean {
    const group = this.groups().find((g) => g.category === category);
    return group?.requirements.some((r) => r.status === IntakeStatus.ReviewFindings) ?? false;
  }

  protected resultLabelKey(result: VerdictResult): string {
    return result === VerdictResult.Pass
      ? 'requirementIntake.verdict.result.pass'
      : 'requirementIntake.verdict.result.fail';
  }

  private resolveVerdictResult(
    verdict: GroupedViewRequirementIntake['items'][number]['verdict'],
  ): VerdictResult | undefined {
    const value = verdict?.finalResult;
    if (value === VerdictResult.Pass) {
      return VerdictResult.Pass;
    }

    if (value === VerdictResult.Fail) {
      return VerdictResult.Fail;
    }

    return undefined;
  }
}
